import { useEffect, useRef, useCallback } from 'react';
import { uploadsService } from '@/shared/api/services/uploads.service';
import {
  VALIDATION_POLL_INTERVAL_MS as POLL_INTERVAL_MS,
  VALIDATION_POLL_INITIAL_DELAY_MS as INITIAL_DELAY_MS,
  VALIDATION_WAIT_TIMEOUT_MS,
} from '@/features/angle/lib/validation-polling';
import type { ValidationItem, UploadPhase } from './types';
import { settledUploadPatch, type UploadRowCopy } from './decode-validation';

interface UseValidationPollingProps {
  /** Building the intakes belong to — the polling endpoint is building-scoped. */
  projectId: string;
  phase: UploadPhase;
  validationItems: ValidationItem[];
  setValidationItems: React.Dispatch<React.SetStateAction<ValidationItem[]>>;
  /** Fallback row text when the server sent none. */
  copy: UploadRowCopy;
  validationTimeoutMessage: string;
}

export function useValidationPolling({
  projectId,
  phase,
  validationItems,
  setValidationItems,
  copy,
  validationTimeoutMessage,
}: UseValidationPollingProps) {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingDelayRef = useRef<NodeJS.Timeout | null>(null);
  // Time each intake has been watched, counted from the first poll tick —
  // queue time included. A job the queue never picks up leaves the intake in
  // `pending` forever, and a budget that only ran while `processing` never
  // expired for it: the modal sat with no buttons until the tab was reloaded.
  const waitedElapsedRef = useRef<Map<string, number>>(new Map());

  // Use ref for validationItems to avoid stale closure in setInterval
  const validationItemsRef = useRef(validationItems);
  validationItemsRef.current = validationItems;

  // Fallback copy is read, never re-subscribed on: a caller passing a fresh
  // object each render must not restart the timer (and its 10s delay with it).
  const copyRef = useRef(copy);
  copyRef.current = copy;

  // The effect must NOT depend on the items array: a new array arrives on every
  // server answer, and re-running the effect tore the interval down (cleanup)
  // while leaving the ref set, so the `!pollingIntervalRef.current` guard
  // refused to start it again. One settled file killed the poll for the rest of
  // the batch. Items are read through a ref; only this boolean gates the timer.
  const hasValidatingItems = validationItems.some(
    (item) => item.status === 'validating'
  );

  useEffect(() => {
    if (phase !== 'validating' || !hasValidatingItems) {
      if (pollingDelayRef.current) {
        clearTimeout(pollingDelayRef.current);
        pollingDelayRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    if (!pollingIntervalRef.current) {
      waitedElapsedRef.current = new Map();

      // Wait before starting polling — unified across all platforms
      pollingDelayRef.current = setTimeout(() => {
        pollingIntervalRef.current = setInterval(async () => {
          // Read from ref to avoid stale closure
          const itemsToCheck = validationItemsRef.current.filter(
            (item) => item.status === 'validating'
          );

          if (itemsToCheck.length === 0) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            return;
          }

          for (const item of itemsToCheck) {
            try {
              const upload = await uploadsService.get(projectId, item.id);
              const validation = upload.attributes?.validation;

              if (!validation || validation.is_in_progress !== false) {
                // Queued, running, or not even started — one budget covers all
                // three, so an intake the queue never picks up ends with an
                // answer instead of spinning until the tab is reloaded.
                const elapsed =
                  (waitedElapsedRef.current.get(item.id) ?? 0) +
                  POLL_INTERVAL_MS;
                waitedElapsedRef.current.set(item.id, elapsed);

                if (elapsed >= VALIDATION_WAIT_TIMEOUT_MS) {
                  setValidationItems((prev) =>
                    prev.map((vi) =>
                      vi.id === item.id
                        ? {
                            ...vi,
                            status: 'error' as const,
                            error: validationTimeoutMessage,
                          }
                        : vi
                    )
                  );
                }
                continue;
              }

              // The server says whether to ask again — the set of terminal
              // statuses has grown four times, and every client that kept its
              // own copy of that list polled forever on the next addition.
              const patch = settledUploadPatch(upload, copyRef.current);
              if (patch) {
                setValidationItems((prev) =>
                  prev.map((vi) => (vi.id === item.id ? { ...vi, ...patch } : vi))
                );
              }
            } catch (error) {
              console.error(
                `[useValidationPolling] Polling error for upload ${item.id}:`,
                error
              );
            }
          }
        }, POLL_INTERVAL_MS);
      }, INITIAL_DELAY_MS);
    }

    return () => {
      if (pollingDelayRef.current) {
        clearTimeout(pollingDelayRef.current);
        pollingDelayRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    projectId,
    phase,
    hasValidatingItems,
    setValidationItems,
    validationTimeoutMessage,
  ]);

  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingDelayRef.current) {
      clearTimeout(pollingDelayRef.current);
      pollingDelayRef.current = null;
    }
  }, []);

  return { clearPolling };
}
